import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  CircularProgress,
} from '@mui/material';
// import supabase from '../utils/supabaseClient';
import proxyClient, { getAccessToken } from '../utils/proxyClient';
import { useAuth } from '../App';
import AppHeader from './AppHeader';
import Sidebar from './Sidebar';
import PageBackground from './PageBackground';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const checkAdmin = async () => {
      if (user) {
        try {
          // Get token using the exported function
          const authToken = getAccessToken();
          
          // Make API call to the backend endpoint
          const response = await fetch('/api/auth/profile', {
            headers: {
              'Authorization': `Bearer ${authToken}`
            }
          });
          
          if (!response.ok) {
            throw new Error('Failed to fetch profile');
          }
          
          const profile = await response.json();
          setIsAdmin(profile?.role === 'admin');
        } catch (error) {
          console.error('Error checking admin status:', error);
        }
      }
      setLoading(false);
    };

    checkAdmin();
  }, [user]);

  const handleTabChange = (tab: string) => {
    switch (tab) {
      case 'dashboard':
        navigate('/');
        break;
      case 'borrowerForm':
        navigate('/borrower-form');
        break;
      case 'uwNotes':
        navigate('/uw-notes');
        break;
      case 'appraisalCriticality':
        navigate('/appraisal-criticality');
        break;
      case 'appraisalCR':
        navigate('/appraisal-cr');
        break;
      case 'exceptionWriting':
        navigate('/exception-writing');
        break;
      case 'businessNotes':
        navigate('/business-notes');
        break;
      case 'ocrReader':
        navigate('/ocr-reader');
        break;
      case 'easyAccess':
        navigate('/easy-access');
        break;
      case 'downloads':
        navigate('/downloads');
        break;
      case 'admin':
        navigate('/admin');
        break;
      default:
        navigate('/');
    }
  };

  const handleLogout = async () => {
    try {
      // Set a flag to indicate logout is in progress
      sessionStorage.setItem('logoutInProgress', 'true');
      
      // Sign out from Supabase through the proxy client
      await proxyClient.auth.signOut();
      
      // Force immediate navigation to login page
      console.log('Layout: User logged out, navigating to login page');
      navigate('/login');
    } catch (error) {
      console.error('Error during logout:', error);
      // Still navigate to login page even if there's an error
      navigate('/login');
    }
  };

  if (loading || authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppHeader />
        <Container component="main" sx={{ flex: 1, py: 4 }}>
          {children}
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <AppHeader />
      <Sidebar onTabChange={handleTabChange} onLogout={handleLogout} isAdmin={isAdmin} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: '100px', // Match the header height
        }}
      >
        <PageBackground>
          {children}
        </PageBackground>
      </Box>
    </Box>
  );
};

export default Layout; 